// src/pages/AdminPanelPage.tsx
// Painel Administrativo - IDENTIDADE GRAIA

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AdminPanelPage.css';
import '../components/admin/admin-shared.css';

// Componentes
import CustomModal from '../components/CustomModal';
import AdminHeader from '../components/admin/AdminHeader';
import AdminGroups from '../components/admin/AdminGroups';
import AdminStats from '../components/admin/AdminStats';
import AdminUsersNew from '../components/admin/AdminUsersNew';
import AdminErrors from '../components/admin/AdminErrors';
import AdminNotifications from '../components/admin/AdminNotifications';
import AdminSales from '../components/admin/AdminSales';
import AdminSubscriptions from '../components/admin/AdminSubscriptions';
import AdminMarketplace from '../components/admin/AdminMarketplace';
import AdminPlatformManagement from '../components/admin/AdminPlatformManagement';
import AdminContentMap from '../components/admin/AdminContentMap';
import ProductsAdmin from '../components/admin/products/ProductsAdmin';

// Types
import { SystemStats, GroupData, UserData, ErrorReport, AdminPage } from '../components/admin/types';

const API_BASE_URL = '/api';


const AdminPanelPage: React.FC = () => {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    const [activePage, setActivePage] = useState<AdminPage>('stats');

    const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
    const [allGroups, setAllGroups] = useState<GroupData[]>([]);
    const [allUsers, setAllUsers] = useState<UserData[]>([]);
    const [errorReports, setErrorReports] = useState<ErrorReport[]>([]);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState<{
        title: string;
        message: string;
        danger: boolean;
        onConfirm: () => void;
    } | null>(null);

    const [modalData, setModalData] = useState<{
        isOpen: boolean;
        icon: string;
        title: string;
        message: string;
        type: 'success' | 'danger' | 'info';
        onConfirm: () => void;
    } | null>(null);

    useEffect(() => {
        if (!isAdmin) {
            navigate('/dashboard');
            return;
        }
        loadAdminData();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (activePage === 'errors') {
            fetchErrors();
        }
    }, [activePage]);

    // ========== CARREGAMENTO ==========

    const loadAdminData = async () => {
        try {
            const token = localStorage.getItem('accessToken');

            const statsRes = await fetch(`${API_BASE_URL}/admin/system/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (statsRes.ok) {
                setSystemStats(await statsRes.json());
            }

            const groupsRes = await fetch(`${API_BASE_URL}/admin/groups/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (groupsRes.ok) {
                const data = await groupsRes.json();
                setAllGroups(data.groups);
            }

            const usersRes = await fetch(`${API_BASE_URL}/admin/users/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (usersRes.ok) {
                const data = await usersRes.json();
                setAllUsers(data.users);
            }

            const errorsRes = await fetch(`${API_BASE_URL}/admin/errors/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (errorsRes.ok) {
                const data = await errorsRes.json();
                setErrorReports(data.errors);
            }

        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        }
    };

    const fetchErrors = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/admin/errors/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setErrorReports(data.errors);
            }
        } catch (error) {
            console.error('Erro ao buscar erros:', error);
        }
    };

    // ========== CONFIRMAÇÃO ==========

    const confirmThen = (title: string, message: string, danger: boolean, action: () => void) => {
        setConfirmAction({ title, message, danger, onConfirm: action });
        setShowConfirmDialog(true);
    };

    const executeConfirmedAction = () => {
        if (confirmAction) {
            confirmAction.onConfirm();
        }
        setShowConfirmDialog(false);
        setConfirmAction(null);
    };

    const showSuccessModal = (title: string, message: string) => {
        setModalData({
            isOpen: true,
            icon: '✓',
            title,
            message,
            type: 'success',
            onConfirm: () => setModalData(null)
        });
    };

    const showErrorModal = (title: string, message: string) => {
        setModalData({
            isOpen: true,
            icon: '✕',
            title,
            message,
            type: 'danger',
            onConfirm: () => setModalData(null)
        });
    };

    // ========== GRUPOS ==========

    const deleteGroup = async (groupId: string, groupName: string) => {
        confirmThen(
            'Deletar Grupo',
            `Tem certeza que deseja deletar o grupo "${groupName}"?`,
            true,
            async () => {
                try {
                    const token = localStorage.getItem('accessToken');
                    const response = await fetch(`${API_BASE_URL}/admin/groups/${groupId}/force-delete`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.ok) {
                        setAllGroups(allGroups.filter(g => g._id !== groupId));
                        showSuccessModal('Grupo Deletado', 'O grupo foi removido com sucesso.');
                    } else {
                        showErrorModal('Erro', 'Não foi possível deletar o grupo.');
                    }
                } catch (error) {
                    console.error('Erro ao deletar grupo:', error);
                    showErrorModal('Erro', 'Erro ao deletar grupo.');
                }
            }
        );
    };

    // ========== ESTATÍSTICAS ==========

    const resetExcludedCards = async () => {
        confirmThen(
            'Resetar Flashcards Excluídos',
            'Isso irá limpar o progresso de flashcards órfãos (deletados). Confirmar?',
            true,
            async () => {
                try {
                    const token = localStorage.getItem('accessToken');
                    const response = await fetch(`${API_BASE_URL}/admin/stats/clean-orphaned-progress`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.ok) {
                        showSuccessModal('Resetado', 'Flashcards órfãos foram removidos.');
                    }
                } catch (error) {
                    console.error('Erro:', error);
                    showErrorModal('Erro', 'Não foi possível resetar.');
                }
            }
        );
    };

    const resetMyStats = async () => {
        confirmThen(
            'Resetar Minhas Estatísticas',
            'Isso irá resetar todas as suas estatísticas pessoais. Confirmar?',
            true,
            async () => {
                try {
                    const token = localStorage.getItem('accessToken');
                    const response = await fetch(`${API_BASE_URL}/admin/stats/reset-my-stats`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.ok) {
                        showSuccessModal('Resetado', 'Suas estatísticas foram resetadas.');
                    }
                } catch (error) {
                    console.error('Erro:', error);
                    showErrorModal('Erro', 'Não foi possível resetar.');
                }
            }
        );
    };

    const resetGlobalStats = async () => {
        confirmThen(
            'Resetar Estatísticas Globais',
            'Isso irá deletar todas as estatísticas de todos os usuários. Esta ação é irreversível. Tem absoluta certeza?',
            true,
            async () => {
                try {
                    const token = localStorage.getItem('accessToken');

                    await fetch(`${API_BASE_URL}/admin/stats/reset-all-progress`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    await fetch(`${API_BASE_URL}/admin/stats/reset-all-daily-stats`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    for (const user of allUsers) {
                        await fetch(`${API_BASE_URL}/admin/stats/reset-user-stats/${user._id}`, {
                            method: 'DELETE',
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    }

                    showSuccessModal('Resetado', 'Todas as estatísticas globais foram resetadas.');
                    loadAdminData();

                } catch (error) {
                    console.error('Erro:', error);
                    showErrorModal('Erro', 'Não foi possível resetar.');
                }
            }
        );
    };

    const resetUserStats = async (userId: string, username: string) => {
        confirmThen(
            'Resetar Estatísticas do Usuário',
            `Deseja resetar as estatísticas de ${username}?`,
            true,
            async () => {
                try {
                    const token = localStorage.getItem('accessToken');
                    const response = await fetch(`${API_BASE_URL}/admin/stats/reset-user-stats/${userId}`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.ok) {
                        showSuccessModal('Resetado', `Estatísticas de ${username} foram resetadas.`);
                        loadAdminData();
                    }
                } catch (error) {
                    console.error('Erro:', error);
                    showErrorModal('Erro', 'Não foi possível resetar.');
                }
            }
        );
    };

    // ========== ERROS ==========
    const updateErrorStatus = async (errorId: string, status: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/admin/errors/${errorId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ status })
            });

            if (response.ok) {
                fetchErrors();
                showSuccessModal('Status Atualizado', 'O status do erro foi alterado.');
            }
        } catch (error) {
            console.error('Erro:', error);
            showErrorModal('Erro', 'Não foi possível atualizar status.');
        }
    };

    const deleteError = async (errorId: string) => {
        confirmThen(
            'Deletar Erro',
            'Deseja deletar este report de erro?',
            true,
            async () => {
                try {
                    const token = localStorage.getItem('accessToken');
                    const response = await fetch(`${API_BASE_URL}/admin/errors/${errorId}`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.ok) {
                        fetchErrors();
                        showSuccessModal('Deletado', 'Erro removido com sucesso.');
                    }
                } catch (error) {
                    console.error('Erro:', error);
                    showErrorModal('Erro', 'Não foi possível deletar.');
                }
            }
        );
    };

    // ========== NOTIFICAÇÕES ==========

    const sendNotification = async (title: string, message: string, userIds: string[] | null) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/notifications/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    title,
                    message,
                    userIds
                })
            });

            if (response.ok) {
                const data = await response.json();
                showSuccessModal(
                    'Notificação Enviada',
                    `A notificação foi enviada para ${data.count} usuário(s).`
                );
            } else {
                showErrorModal('Erro', 'Não foi possível enviar a notificação.');
            }
        } catch (error) {
            console.error('Erro:', error);
            showErrorModal('Erro', 'Erro ao enviar notificação.');
        }
    };

    // ========== RENDER ==========
    const pendingErrors = errorReports.filter(e => e.status === 'pending').length;

    return (
        <div className="admin-page-main-container">
            <AdminHeader
                activePage={activePage}
                onPageChange={setActivePage}
                errorCount={pendingErrors}
            />

            <main className="admin-main-content">
                {activePage === 'stats' && (
                    <AdminStats
                        systemStats={systemStats}
                        allUsers={allUsers}
                        onResetExcludedCards={resetExcludedCards}
                        onResetMyStats={resetMyStats}
                        onResetGlobalStats={resetGlobalStats}
                        onResetUserStats={resetUserStats}
                    />
                )}

                {activePage === 'platform-management' && (
                    <AdminPlatformManagement
                        onShowToast={(msg, type) => {
                            if (type === 'error') showErrorModal('Erro', msg);
                            else showSuccessModal('Sucesso', msg);
                        }}
                    />
                )}

                {activePage === 'content-map' && (
                    <AdminContentMap
                        onShowToast={(msg, type) => {
                            if (type === 'error') showErrorModal('Erro', msg);
                            else showSuccessModal('Sucesso', msg);
                        }}
                    />
                )}

                {activePage === 'users' && (
                    <AdminUsersNew />
                )}

                {activePage === 'groups' && (
                    <AdminGroups
                        groups={allGroups}
                        totalGroups={systemStats?.groups.total || 0}
                        onDeleteGroup={deleteGroup}
                        onShowToast={(msg, type) => {
                            if (type === 'error') showErrorModal('Erro', msg);
                            else showSuccessModal('Sucesso', msg);
                        }}
                    />
                )}

                {activePage === 'errors' && (
                    <AdminErrors
                        errors={errorReports}
                        onUpdateStatus={updateErrorStatus}
                        onDeleteError={deleteError}
                    />
                )}

                {activePage === 'notifications' && (
                    <AdminNotifications
                        onSendNotification={sendNotification}
                    />
                )}

                {activePage === 'sales' && (
                    <AdminSales
                        onShowToast={(msg, type) => {
                            if (type === 'error') showErrorModal('Erro', msg);
                            else showSuccessModal('Sucesso', msg);
                        }}
                    />
                )}

                {activePage === 'products-admin' && (
                    <ProductsAdmin onShowToast={(msg, type) => {
                        if (type === 'error') showErrorModal('Erro', msg);
                        else showSuccessModal('Sucesso', msg);
                    }} />
                )}

                {activePage === 'marketplace' && (
                    <AdminMarketplace
                        onShowToast={(msg, type) => {
                            if (type === 'error') showErrorModal('Erro', msg);
                            else if (type === 'info') showSuccessModal('Info', msg);
                            else showSuccessModal('Sucesso', msg);
                        }}
                    />
                )}

                {activePage === 'subscriptions' && <AdminSubscriptions />}
            </main>

            {showConfirmDialog && confirmAction && (
                <div className="admin-dialog-overlay">
                    <div className="admin-dialog">
                        <h2>{confirmAction.title}</h2>
                        <p>{confirmAction.message}</p>
                        <div className="admin-dialog-actions">
                            <button onClick={() => setShowConfirmDialog(false)}>
                                Cancelar
                            </button>
                            <button
                                className={confirmAction.danger ? 'btn-danger' : 'btn-primary'}
                                onClick={executeConfirmedAction}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modalData && (
                <CustomModal
                    isOpen={modalData.isOpen}
                    icon={modalData.icon}
                    title={modalData.title}
                    message={modalData.message}
                    type={modalData.type}
                    onConfirm={modalData.onConfirm}
                    onCancel={() => setModalData(null)}
                />
            )}
        </div>
    );
};

export default AdminPanelPage;
